import React, { useState, useCallback, useEffect } from 'react';
import { Modal, TextField, showToast, DatePicker, BlockCard, Button, Alert, Select } from '@trellixio/roaster-coffee';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { formatDate, getUserName } from 'helpers';
import { useAPI } from 'hooks';
import Locales from 'locales';
import styles from './Trips.module.css';

const AddTripModal = ({ isOpened, categories, openCategoriesModal, closeModal }) => {
  const [pilgrimNPI, setNPI] = useState('');
  const [pilgrimPassport, setPassport] = useState('');
  const [pilgrimFirstname, setFirstname] = useState('');
  const [pilgrimLastname, setLastname] = useState('');
  const [pilgrimNote, setpilgrimNote] = useState('');
  const [{ month, year }, setDate] = useState({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  });
  const [pilgrimBirthdate, setBirthdate] = useState(null);
  const [pilgrimPassportExpirationDate, setPassportExpirationDate] = useState(null);
  const [pilgrimIsInternational, setIsInternational] = useState(false);
  const [pilgrimCategory, setPilgrimCategory] = useState(categories[0].id);
  const [npiError, setNpiError] = useState(false);
  const [passportError, setPassportError] = useState(false);
  const [emptyFieldError, setEmptyFieldError] = useState(false);
  const [birthDateError, setBirthDateError] = useState(null);
  const [passportExpirationDateError, setPassportExpirationDateError] = useState(null);
  const [passportRequiredError, setPassportRequiredError] = useState(false);
  const [passportExpirationDateRequiredError, setPassportExpirationDateRequiredError] = useState(false);

  // The passportMinExpirationDate is set to 6 months from today.
  // It is to ensure that the passport expiration date has at least 6 months of validity.
  const passportMinExpirationDate = new Date(new Date().setMonth(new Date().getMonth() + 6));

  const { api } = useAPI();
  const queryClient = useQueryClient();
  const isMutating = queryClient.isMutating() > 0;

  const selectedCategory = categories.find((category) => category.id === pilgrimCategory);

  const newPilgrim = {
    pilgrim: {
      first_name: pilgrimFirstname,
      last_name: pilgrimLastname ? pilgrimLastname.toUpperCase() : '',
      npi: pilgrimNPI,
      passport: pilgrimPassport ? pilgrimPassport.toUpperCase() : '',
      birthdate: formatDate(pilgrimBirthdate, 'YYYY-MM-DD'),
      passport_expiration: pilgrimPassportExpirationDate
        ? formatDate(pilgrimPassportExpirationDate, 'YYYY-MM-DD')
        : null,
      is_international: pilgrimIsInternational,
    },
    note: pilgrimNote,
    category_id: pilgrimCategory,
  };

  const { mutate: createPilgrim } = useMutation({
    mutationFn: () => api.create('trip', newPilgrim),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trip/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/stats/default/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/stats/category/'] });
      showToast({
        message: Locales.formatString(Locales.trip.toast.pilgrim_created, {
          fullname: getUserName(pilgrimFirstname, pilgrimLastname),
        }),
        level: 'success',
      });
      closeModal();
    },
  });

  const validateForm = (event) => {
    event.preventDefault();

    const isNPIMissing = pilgrimNPI === '';
    const isPassportProvided = pilgrimPassport !== '';

    const npiCorrectFormat = isNPIMissing || /^[0-9]{10}$/.test(pilgrimNPI);
    const passportCorrectFormat = !isPassportProvided || /^[A-Z0-9]{8,9}$/.test(pilgrimPassport);

    const npiErrorLocal = pilgrimNPI !== '' && !npiCorrectFormat;
    const passportErrorLocal = isPassportProvided && !passportCorrectFormat;
    const emptyPassportOrNpiFieldErrorLocal = !isPassportProvided && isNPIMissing;
    const emptyFieldErrorLocal =
      pilgrimFirstname.length === 0 ||
      pilgrimLastname.length === 0 ||
      pilgrimBirthdate === null ||
      emptyPassportOrNpiFieldErrorLocal;
    // passport is required if passport expiration is provided
    const passportRequiredErrorLocal = !isPassportProvided && pilgrimPassportExpirationDate !== null;
    // passport expiration date is required if passport is provided
    const passportExpirationDateRequiredErrorLocal = isPassportProvided && pilgrimPassportExpirationDate === null;

    setNpiError(npiErrorLocal);
    setPassportError(passportErrorLocal);
    setEmptyFieldError(emptyFieldErrorLocal);
    setPassportRequiredError(passportRequiredErrorLocal);
    setPassportExpirationDateRequiredError(passportExpirationDateRequiredErrorLocal);

    if (
      !npiErrorLocal &&
      !emptyFieldErrorLocal &&
      !passportErrorLocal &&
      !passportRequiredErrorLocal &&
      !passportExpirationDateRequiredErrorLocal &&
      birthDateError === null &&
      passportExpirationDateError === null
    ) {
      createPilgrim();
    }
  };

  const handleMonthChange = useCallback(
    (targetMonth, targetYear) => setDate({ month: targetMonth, year: targetYear }),
    []
  );

  const handleBirthDateInputError = useCallback((errorType) => {
    if (errorType === 'OUT_OF_RANGE') {
      setBirthDateError(Locales.trip.errors.out_of_range_birthdate);
      return Locales.trip.errors.out_of_range_birthdate;
    }
    setBirthDateError(Locales.trip.errors.date);
    return Locales.trip.errors.date;
  }, []);

  const handleExpirationDateInputError = useCallback((errorType) => {
    if (errorType === 'OUT_OF_RANGE') {
      setPassportExpirationDateError(Locales.trip.errors.out_of_range_expiration_date);
      return Locales.trip.errors.out_of_range_expiration_date;
    }
    setPassportExpirationDateError(Locales.trip.errors.date);
    return Locales.trip.errors.date;
  }, []);

  const handleKeyDown = (e, callback) => {
    if (e.keyCode === 13) {
      callback();
    }
  };

  const handleExpirationDateChange = (date) => {
    setPassportExpirationDate(date);
    setPassportExpirationDateError(null);
    setPassportExpirationDateRequiredError(false);
  };

  const handlePassportChange = (value) => {
    setPassport(value);
    setPassportRequiredError(false);
  };

  const handleBirthDateChange = (date) => {
    setBirthdate(date);
    setBirthDateError(null);
  };

  useEffect(() => {
    setIsInternational(selectedCategory?.name.includes('Diaspora'));
  }, [pilgrimCategory]);

  return (
    <Modal
      title={Locales.trip.register_pilgrim}
      size="small"
      opened={isOpened}
      onClose={closeModal}
      footer={
        <>
          <Button variant="secondary" onClick={closeModal}>
            {Locales.base.button.discard}
          </Button>
          <Button variant="primary" color="success" disabled={isMutating} onClick={validateForm}>
            {Locales.base.button.save}
          </Button>
        </>
      }
    >
      <form>
        <BlockCard>
          {!passportRequiredError && !passportExpirationDateRequiredError && emptyFieldError && (
            <Alert level="error" message={Locales.trip.errors.emptyfield} />
          )}
          {passportExpirationDateRequiredError && (
            <Alert level="error" message={Locales.trip.errors.passport_expiration_date_required} />
          )}
          {passportRequiredError && <Alert level="error" message={Locales.trip.errors.passport_required} />}

          <p>{Locales.trip.enter_pilgrim_details} :</p>

          <TextField
            label={Locales.base.field.npi}
            type="text"
            placeholder={Locales.base.field.placeholder.npi}
            pattern="^[0-9]{10}$"
            maxLength="10"
            minLength="10"
            error={npiError ? Locales.trip.errors.npi : ''}
            onChange={setNPI}
          />

          <TextField
            label={Locales.base.field.passport}
            type="text"
            placeholder={Locales.base.field.placeholder.passport}
            pattern="^[A-Z0-9]{8,9}$"
            minLength="7"
            maxLength="10"
            error={passportError ? Locales.trip.errors.passport : ''}
            onChange={handlePassportChange}
          />

          <div className="items-group">
            <TextField
              label={Locales.base.field.first_name}
              type="text"
              placeholder={Locales.base.field.placeholder.firstname}
              onChange={setFirstname}
              required
            />

            <TextField
              label={Locales.base.field.last_name}
              type="text"
              placeholder={Locales.base.field.placeholder.lastname}
              onChange={setLastname}
              required
            />
          </div>

          <DatePicker
            label={Locales.base.field.birthdate}
            placeholder={Locales.base.field.placeholder.date}
            month={month}
            year={year}
            onChange={handleBirthDateChange}
            onMonthChange={handleMonthChange}
            selected={pilgrimBirthdate}
            maxDate={new Date()}
            onInputError={handleBirthDateInputError}
            required
          />

          <DatePicker
            label={Locales.base.field.passport_expiration}
            placeholder={Locales.base.field.placeholder.date}
            month={month}
            year={year}
            onChange={handleExpirationDateChange}
            onMonthChange={handleMonthChange}
            selected={pilgrimPassportExpirationDate}
            minDate={passportMinExpirationDate}
            onInputError={handleExpirationDateInputError}
          />

          <Select
            label={Locales.trip.table.categorie}
            data={categories}
            helpText={Locales.formatString(Locales.trip.categories.help_text, {
              details: (
                <div
                  className={styles.categoriesModalTrigger}
                  role="button"
                  tabIndex={0}
                  onClick={openCategoriesModal}
                  onKeyDown={(e) => handleKeyDown(e, openCategoriesModal)}
                >
                  {Locales.trip.categories.help_text_btn}
                </div>
              ),
            })}
            onChange={setPilgrimCategory}
          />

          <TextField label={Locales.base.field.note} type="text" placeholder="..." onChange={setpilgrimNote} />
        </BlockCard>
      </form>
    </Modal>
  );
};

AddTripModal.defaultProps = {
  isOpened: false,
  categories: null,
  openCategoriesModal: null,
  closeModal: null,
};

AddTripModal.propTypes = {
  isOpened: PropTypes.bool,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    })
  ),
  openCategoriesModal: PropTypes.func,
  closeModal: PropTypes.func,
};

export default AddTripModal;
